import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Tabs as MUITabs, Tab } from '@mui/material';
import useIsMobile from '../../../hooks/useIsMobile';

/**
 * Custom hook for managing tab navigation with URL synchronization
 *
 * This hook provides tab management functionality that automatically syncs the current tab
 * with the browser URL and handles tab switching with navigation updates. The hook initializes
 * the current tab based on the current URL path and updates the URL when tabs are switched.
 *
 * @param {Array<Object>} tabs - Array of tab configuration objects
 * @param {string} tabs[].label - Display text for the tab
 * @param {string} tabs[].url - URL path to navigate to when tab is selected
 * @param {Object} [tabs[].labelSx] - Optional Material-UI sx prop for tab label styling
 * @param {boolean} [tabs[].hide] - Optional flag to hide the tab (renders with hidden visibility)
 *
 * @returns {Object} Hook return object containing:
 * @returns {number} returns.currentTab - Index of the currently active tab (0-based)
 * @returns {Function} returns.setCurrentTab - Function to programmatically set the current tab by index
 * @returns {React.Component} returns.Tabs - Pre-configured Material-UI Tabs component with change handler
 *
 * @example
 * const tabs = [
 *   { label: 'Overview', url: '/dashboard/overview' },
 *   { label: 'Analytics', url: '/dashboard/analytics', labelSx: { color: 'primary.main' } },
 *   { label: 'Settings', url: '/dashboard/settings' },
 *   { label: 'Hidden Tab', url: '/dashboard/hidden', hide: true }
 * ];
 *
 * const { currentTab, setCurrentTab, Tabs } = useTabs(tabs);
 *
 * return (
 *   <div>
 *     <Tabs />
 *     <div>Current tab index: {currentTab}</div>
 *     <button onClick={() => setCurrentTab(1)}>Go to Analytics</button>
 *   </div>
 * );
 */
const useTabs = (tabs) => {
    const history = useHistory();
    const [currentTab, setCurrentTab] = useState(0);
    const isMobile = useIsMobile();

    useEffect(() => {
        const initialTab = tabs.findIndex(({ url }) => history.location.pathname.includes(url));
        if (initialTab >= 0) setCurrentTab(initialTab);
    }, []);

    const handleTabChange = (value) => {
        if (tabs[value].url) history.replace(tabs[value].url);
        setCurrentTab(value);
    };

    const Tabs = useMemo(
        () => (
            <MUITabs value={currentTab} onChange={(_e, value) => handleTabChange(value)} aria-label="basic tabs example" sx={{ ...(isMobile && { ml: '-64px' }) }}>
                {tabs.map(({ label, labelSx, hide }, index) => (
                    <Tab label={label} sx={{ ...labelSx, ...(hide ? { visibility: 'hidden', position: 'absolute', left: '-9999px' } : {}) }} value={index} key={label} />
                ))}
            </MUITabs>
        ),
        [tabs, currentTab]
    );

    return {
        currentTab,
        setCurrentTab: handleTabChange,
        Tabs,
    };
};

export default useTabs;
